/**
 * Site information architecture: one table drives the header, the menu
 * panel, the footer and the /tools index, so a destination is added once.
 *
 * Five top-level destinations (PRIMARY_NAV); everything else lives in a
 * group under one of them or in SECONDARY_NAV / LEGAL_NAV. Every route the
 * old header + home page linked to is listed here, so any feature is at most
 * two clicks away (Menu → item).
 */
import { ROUTES } from "../lib/appRoutes";
import { LCBCDOCK_ORIGIN } from "../lib/externalApis";

export type Shape = "circle" | "square" | "triangle";
/** `brand` follows the per-app accent (--ev-brand, see the .ev-home-* themes). */
export type Accent = "red" | "yellow" | "blue" | "brand";
export type Glyph =
  | "atom" | "orbital" | "workbench" | "density" | "tools" | "learn" | "search" | "start"
  | "proof" | "articles" | "pricing" | "hardware" | "shop" | "free" | "plan" | "unlimited"
  | "playground" | "microscope" | "lab" | "compass" | "school" | "components"
  | "ar" | "cube" | "remote" | "quiz" | "table" | "scale" | "calculator" | "scan" | "tap" | "move"
  | "rotate" | "bulb" | "stack" | "devices" | "focus";

export type NavItem = {
  label: string;
  /** Internal route (react-router). */
  to?: string;
  /** External URL (opens in a new tab). */
  href?: string;
  desc?: string;
  /** Hidden inside Apple native shells (App Review 3.1.1, see Footer). */
  appleHidden?: boolean;
  /** Hidden in any native WebView shell (web-only commerce, e.g. pricing). */
  nativeHidden?: boolean;
};

export type NavGroup = {
  id: string;
  label: string;
  /** Where the top-level link goes. */
  to: string;
  shape: Shape;
  glyph: Glyph;
  accent: Accent;
  blurb: string;
  items: NavItem[];
};

/** Path prefixes that mark a group as "current" in the header. */
export const GROUP_MATCH: Record<string, RegExp> = {
  explore: /^\/(molecules|molecule\/|renderer|quantum-orbitals|compare)/,
  workbench: /^\/(workbench|playground)/,
  density: /^\/density/,
  tools: /^\/(tools|periodic-table|molar-mass|balance|reaction|stoichiometry|acid-base|config-builder|quiz|self-ionization)/,
  learn: /^\/(blog|article|docs)/,
};

export const PRIMARY_NAV: NavGroup[] = [
  {
    id: "explore",
    label: "3D Orbital Viewer",
    to: ROUTES.molecules,
    shape: "circle",
    glyph: "atom",
    accent: "red",
    blurb: "Atomic and molecular orbitals in 3D.",
    items: [
      { label: "Molecule library", to: ROUTES.molecules, desc: "Every molecule with its orbitals in 3D" },
      { label: "Atom library", to: "/?view=atoms", desc: "Every element: atomic orbitals on the periodic table" },
      { label: "Molecular orbitals", to: "/?view=molecules", desc: "HOMO, LUMO and energy diagrams" },
      { label: "Quantum numbers", to: ROUTES.quantumOrbitals, desc: "Choose n, l, m directly" },
      { label: "Spotlight viewer", to: ROUTES.renderer, desc: "The 3D viewer, last selection" },
      { label: "Compare molecules", to: ROUTES.compare, desc: "Two structures side by side" },
    ],
  },
  {
    id: "workbench",
    label: "Molecule Builder",
    to: ROUTES.workbench,
    shape: "square",
    glyph: "workbench",
    accent: "yellow",
    blurb: "Build any structure and compute it.",
    items: [
      { label: "Molecule Builder", to: ROUTES.workbench, desc: "Draw, edit and optimise, then compute with GPAW" },
      { label: "Playground", to: ROUTES.playground, desc: "Sketch, SMILES, quick 3D" },
    ],
  },
  {
    id: "density",
    label: "DFT Density",
    to: "/density",
    shape: "triangle",
    glyph: "density",
    accent: "blue",
    blurb: "Electron density maps from real DFT.",
    items: [{ label: "DFT Density", to: "/density", desc: "Electron density of any molecule, as a dot cloud" }],
  },
  {
    id: "tools",
    label: "Tools",
    to: ROUTES.tools,
    shape: "square",
    glyph: "tools",
    accent: "red",
    blurb: "Calculators and practice. No credits used.",
    items: [
      { label: "Periodic table explorer", to: ROUTES.periodicTable, desc: "Trends and element data" },
      { label: "Molar mass", to: ROUTES.molarMass, desc: "From any formula" },
      { label: "Reaction balancer", to: ROUTES.balance, desc: "Balance chemical equations" },
      { label: "Reaction pathway", to: ROUTES.reaction, desc: "Energy profile of a reaction" },
      { label: "Stoichiometry", to: ROUTES.stoichiometry, desc: "Moles, masses, limiting reagent" },
      { label: "Acid–base", to: ROUTES.acidBase, desc: "pH, pKa and buffers" },
      { label: "Electron configuration", to: ROUTES.configBuilder, desc: "Build configurations shell by shell" },
      { label: "Self-ionisation of water", to: ROUTES.selfIonization, desc: "Autoprotolysis, animated" },
      { label: "Quiz", to: ROUTES.quiz, desc: "Test yourself" },
    ],
  },
  {
    id: "learn",
    label: "Learn",
    to: ROUTES.blog,
    shape: "circle",
    glyph: "learn",
    accent: "blue",
    blurb: "Articles, documentation and the API.",
    items: [
      { label: "Articles", to: ROUTES.blog, desc: "Essays on quantum chemistry" },
      { label: "Docs & API", to: ROUTES.docs, desc: "Methods, endpoints, embedding" },
    ],
  },
];

export const SECONDARY_NAV: NavItem[] = [
  { label: "Pricing", to: `${ROUTES.pricing}?src=menu`, nativeHidden: true },
  { label: "Credits", to: ROUTES.credits, nativeHidden: true },
  { label: "Shop", to: ROUTES.shop, appleHidden: true },
  { label: "Get the apps", to: ROUTES.downloadWeb, nativeHidden: true },
  { label: "Atomizer Embedded", to: "/#embedded" },
  { label: "MolDock", href: LCBCDOCK_ORIGIN },
  { label: "Support", to: ROUTES.support },
];

export const LEGAL_NAV: NavItem[] = [
  { label: "Privacy", to: ROUTES.privacy },
  { label: "Cookies", to: ROUTES.cookies },
  { label: "Terms", to: ROUTES.terms },
  { label: "License", to: ROUTES.license },
  // Apple App Review 3.1.2(c): the EULA must be reachable in-app.
  { label: "EULA", href: "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" },
];

const APPLE_SHELLS = ["atomizer-ar", "atomizer-mac", "atomizer-vision", "electronvisualized-mac"];

export function visibleItems(items: NavItem[], shell: string | null, native: boolean): NavItem[] {
  return items.filter(
    (i) => !(i.appleHidden && APPLE_SHELLS.includes(shell || "")) && !(i.nativeHidden && native)
  );
}

export function isAppleShell(shell: string | null): boolean {
  return APPLE_SHELLS.includes(shell || "");
}
